import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GeoCSVRow {
  Year: string;
  Make: string;
  Model: string;
  Engine: string;
}

interface VehicleCatalogRow {
  year: number;
  make: string;
  model: string;
  engine_packages: string;
  package_type: string;
}

/**
 * Parse Geo CSV format (one engine per row) into grouped format
 */
function parseGeoCSV(text: string): VehicleCatalogRow[] {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(',');
  
  // Map to group engines by year/make/model
  const vehicleMap = new Map<string, {
    year: number;
    make: string;
    model: string;
    engines: string[];
  }>();

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = line.split(',');
    const year = parseInt(values[0]);
    const make = values[1];
    const model = values[2];
    const engine = values[3];

    const key = `${year}-${make}-${model}`;
    
    if (!vehicleMap.has(key)) {
      vehicleMap.set(key, {
        year,
        make,
        model,
        engines: []
      });
    }
    
    vehicleMap.get(key)!.engines.push(engine);
  }

  // Convert to final format with semicolon-separated engines
  const result: VehicleCatalogRow[] = [];
  for (const vehicle of vehicleMap.values()) {
    result.push({
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      engine_packages: vehicle.engines.join('; '),
      package_type: 'Domestic'
    });
  }

  return result;
}

/**
 * Import Geo vehicles from CSV into vehicle_catalog
 */
export async function importGeoVehicles(
  onProgress?: (message: string) => void
): Promise<boolean> {
  try {
    onProgress?.('Starting Geo vehicle import...');

    // Fetch the Geo CSV
    const response = await fetch('/data/geo_ymme.csv');
    if (!response.ok) {
      throw new Error('Failed to fetch Geo vehicle CSV');
    }
    
    const csvText = await response.text();
    onProgress?.('Parsing Geo vehicle data...');
    
    const vehicles = parseGeoCSV(csvText);
    onProgress?.(`Parsed ${vehicles.length} unique Geo vehicles`);

    // Check for existing Geo vehicles
    const { data: existing, error: checkError } = await supabase
      .from('vehicle_catalog')
      .select('year, make, model')
      .eq('make', 'Geo');

    if (checkError) {
      console.error('Error checking existing Geo vehicles:', checkError);
      throw checkError;
    }

    // Filter out duplicates
    const existingKeys = new Set(
      existing?.map(v => `${v.year}-${v.make}-${v.model}`) || []
    );
    
    const newVehicles = vehicles.filter(v => 
      !existingKeys.has(`${v.year}-${v.make}-${v.model}`)
    );

    if (newVehicles.length === 0) {
      onProgress?.('All Geo vehicles already exist in catalog');
      toast.info('Geo vehicles already imported');
      return true;
    }

    onProgress?.(`Importing ${newVehicles.length} new Geo vehicles...`);

    // Insert in batches
    const batchSize = 500;
    for (let i = 0; i < newVehicles.length; i += batchSize) {
      const batch = newVehicles.slice(i, i + batchSize);
      
      const { error: insertError } = await supabase
        .from('vehicle_catalog')
        .insert(batch);

      if (insertError) {
        console.error('Error inserting Geo vehicles:', insertError);
        throw insertError;
      }

      onProgress?.(`Imported ${Math.min(i + batchSize, newVehicles.length)} / ${newVehicles.length} vehicles`);
    }

    onProgress?.('✅ Geo vehicle import complete!');
    toast.success(`Successfully imported ${newVehicles.length} Geo vehicles`);
    return true;

  } catch (error) {
    console.error('Error importing Geo vehicles:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    onProgress?.(`❌ Error: ${errorMessage}`);
    toast.error(`Import failed: ${errorMessage}`);
    return false;
  }
}
